import React, { useCallback, useEffect, useState } from "react";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import {
	getMonitoringTraffic,
	getMonitoringTrafficBounds,
	getMonitoringTrafficCoverage,
	syncMonitoringTraffic,
} from "../../../../services/monitoringApi";
import {
	INLINE_ROWS,
	SOURCE_LABELS,
	STATUS_DISTRIBUTION_ROWS,
	SUB_TABS,
} from "./trafficShape";
import {
	axisMax,
	compareLabel,
	dataDays,
	fmtDateTime,
	fmtNum,
	fmtShortDate,
	sourceLabel,
	toSeries,
} from "./trafficUtils";
import TrafficDateRangePicker, { defaultRangeSelection } from "./TrafficDateRangePicker";
import TrafficConnectorsPanel from "./TrafficConnectorsPanel";
import {
	TrafficCard,
	TrafficKpiCard,
	TrafficTableTh,
} from "./TrafficKpiCard";
import TrafficEngineTrendChart from "./TrafficEngineTrendChart";
import TrafficListDialog from "./TrafficListDialog";
import UrlCopyCell from "./UrlCopyCell";
import SummaryPanel from "./SummaryPanel";
import SearchPanel from "./SearchPanel";
import TraceabilityPanel from "./TraceabilityPanel";

function CoverageNotice({ data }) {
	const dc = data?.data_coverage;
	if (!dc || dc.complete) return null;

	const sources = dc.by_source
		? Object.entries(dc.by_source).filter(([, v]) => v)
		: [];

	return (
		<div className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber">
			{dc.actual ? (
				<>
					<strong>Partial data for this range.</strong> You asked for{" "}
					{fmtShortDate(dc.requested.start)} – {fmtShortDate(dc.requested.end)} (
					{dc.requested.days} days), but we hold{" "}
					<strong>
						{fmtShortDate(dc.actual.start)} – {fmtShortDate(dc.actual.end)}
					</strong>{" "}
					({dc.actual.days} of {dc.requested.days} days). Totals cover only those
					days.
				</>
			) : (
				<>
					<strong>No data for this range.</strong> Nothing is stored for{" "}
					{fmtShortDate(dc.requested.start)} – {fmtShortDate(dc.requested.end)}.
				</>
			)}
			{sources.length > 0 && (
				<div className="mt-1">
					{sources.map(([src, v]) => (
						<span key={src} className="me-3">
							{sourceLabel(src)}: {fmtShortDate(v.start)} – {fmtShortDate(v.end)} (
							{v.days}d)
						</span>
					))}
				</div>
			)}
			<div className="mt-1 text-muted">
				{dc.fetching
					? "Fetching the missing days from the connected sources now — reload in a moment."
					: dc.unfetchable
						? "These days are no longer available from the sources — Cloudflare keeps AI-bot detail for about 8 days. A daily job archives each completed day from now on, so coverage grows going forward."
						: "Missing days will be pulled from the sources on the next refresh."}
			</div>
		</div>
	);
}

export default function MonitoringAiTraffic({ websiteId }) {
	const verifiedOnly = true;

	const [sel, setSel] = useState(defaultRangeSelection);
	const [tab, setTab] = useState("summary");
	const [manageOpen, setManageOpen] = useState(false);
	const [bots, setBots] = useState([]);
	const [data, setData] = useState(null);
	const [bounds, setBounds] = useState(null);
	const [coverageDetail, setCoverageDetail] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [syncBusy, setSyncBusy] = useState(false);
	const [syncQueued, setSyncQueued] = useState(false);
	const [coverageLoading, setCoverageLoading] = useState(false);
	const [pagesOpen, setPagesOpen] = useState(false);
	const [brokenOpen, setBrokenOpen] = useState(false);
	const [reloadKey, setReloadKey] = useState(0);

	const days = dataDays(sel);
	const botsParam = bots.length ? bots.join(",") : undefined;

	const loadTraffic = useCallback(async () => {
		if (!websiteId) return;
		setLoading(true);
		setError(null);
		const res = await getMonitoringTraffic(websiteId, {
			start: sel.range.start,
			end: sel.range.end,
			compareStart: sel.compare?.start,
			compareEnd: sel.compare?.end,
			verifiedOnly,
			assistants: botsParam,
		});
		if (!res.ok) {
			setError(res.message || "Failed to load AI traffic");
			setData(null);
			setLoading(false);
			return;
		}
		setData(res.traffic);
		setSyncQueued(false);
		setLoading(false);
	}, [
		websiteId,
		sel.range.start,
		sel.range.end,
		sel.compare?.start,
		sel.compare?.end,
		botsParam,
	]);

	useEffect(() => {
		loadTraffic();
	}, [loadTraffic, reloadKey]);

	useEffect(() => {
		if (!websiteId) return;
		getMonitoringTrafficBounds(websiteId).then((res) => {
			if (res.ok) setBounds(res.bounds);
		});
	}, [websiteId]);

	useEffect(() => {
		if (!websiteId || !data?.coverage?.has_crawls) {
			setCoverageDetail(null);
			setCoverageLoading(false);
			return;
		}
		setCoverageLoading(true);
		getMonitoringTrafficCoverage(websiteId).then((res) => {
			if (res.ok) setCoverageDetail(res.coverage);
			setCoverageLoading(false);
		});
	}, [websiteId, data?.coverage?.has_crawls, reloadKey]);

	async function handleSync() {
		if (syncBusy) return;
		setSyncBusy(true);
		const res = await syncMonitoringTraffic(
			websiteId,
			Math.max(7, days)
		);
		setSyncBusy(false);
		if (!res.ok) {
			toast.error(res.message || "Sync failed");
			return;
		}
		toast.success("Traffic sync queued");
		setSyncQueued(true);
		setTimeout(() => setReloadKey((k) => k + 1), 6000);
	}

	function handleRangeChange(next) {
		setSyncQueued(false);
		setSel(next);
	}

	if (loading && !data) {
		return <p className="monitoring__ai-traffic-empty mb-0">Loading AI traffic…</p>;
	}
	if (error && !data) {
		return <p className="monitoring__ai-traffic-empty monitoring__empty--error mb-0">{error}</p>;
	}
	if (!data) {
		return <p className="monitoring__ai-traffic-empty mb-0">No AI traffic data yet.</p>;
	}

	const { kpis, coverage: cov } = data;
	const nothingConnected = (cov?.connections?.length ?? 0) === 0;
	const uneven = !!data.comparison?.uneven;
	const cmpLabel = compareLabel(data.comparison);
	const assistantLabels = data.assistant_labels || {};

	const referralPagesNote =
		data.referral_pages_meta && data.referral_pages_meta.total_visits > 0
			? `${data.referral_pages_meta.attributed_visits} of ${data.referral_pages_meta.total_visits} visits attributed to a landing page by GA4${
					data.referral_pages_meta.attributed_visits <
					data.referral_pages_meta.total_visits
						? " — the rest had no landing page recorded, so they appear in the totals but not here."
						: "."
				}`
			: "Landing pages for AI referral visits.";

	const listsTruncated = !!data.lists_truncated;

	return (
		<div className="monitoring__ai-traffic">
			<div className="monitoring__ai-traffic-toolbar">
				<span className="monitoring__ai-traffic-toolbar-label">Data sources</span>
				{(cov?.connections?.length ?? 0) === 0 ? (
					<span className="monitoring__ai-traffic-source-badge monitoring__ai-traffic-source-badge--warn">
						None connected
					</span>
				) : (
					cov.connections.map((c) => (
						<span
							key={c.id}
							className="monitoring__ai-traffic-source-badge monitoring__ai-traffic-source-badge--connected"
						>
							{SOURCE_LABELS[c.connector_key] ?? c.connector_key}
							{c.transport ? ` · ${c.transport}` : ""}
						</span>
					))
				)}
				{cov?.missing?.length > 0 && (
					<span className="monitoring__ai-traffic-toolbar-hint">
						Add {cov.missing.join(" + ")} for full coverage.
					</span>
				)}
				<Button
					size="sm"
					color="primary"
					className="monitoring__action-btn monitoring__action-btn--primary"
					disabled={syncBusy}
					onClick={handleSync}
				>
					{syncBusy ? "Syncing…" : syncQueued ? "Sync queued" : "Sync now"}
				</Button>
				<Button
					size="sm"
					color="primary"
					className="monitoring__action-btn monitoring__action-btn--primary"
					onClick={() => setManageOpen((v) => !v)}
				>
					{manageOpen ? "Hide sources" : "Manage sources"}
				</Button>
				<div className="monitoring__ai-traffic-toolbar-spacer" />
				<TrafficDateRangePicker
					value={sel}
					onChange={handleRangeChange}
					minDate={bounds?.overall?.start}
					maxDate={bounds?.today}
				/>
			</div>

			{tab !== "search" && (data.available_assistants?.length ?? 0) > 1 && (
				<div className="monitoring__ai-traffic-bot-filter">
					<span className="monitoring__ai-traffic-filter-label">AI bots</span>
					<button
						type="button"
						className={`monitoring__ai-traffic-pill${
							bots.length === 0 ? " monitoring__ai-traffic-pill--active" : ""
						}`}
						onClick={() => setBots([])}
					>
						All
					</button>
					{data.available_assistants.map((a) => {
						const on = bots.includes(a);
						return (
							<button
								key={a}
								type="button"
								className={`monitoring__ai-traffic-pill${
									on ? " monitoring__ai-traffic-pill--active" : ""
								}`}
								onClick={() =>
									setBots(on ? bots.filter((x) => x !== a) : [...bots, a])
								}
							>
								{assistantLabels[a] ?? a}
							</button>
						);
					})}
					{bots.length > 0 && (
						<span className="monitoring__ai-traffic-bot-scope-hint">
							every figure below is limited to {bots.length} bot
							{bots.length === 1 ? "" : "s"}
						</span>
					)}
				</div>
			)}

			{tab !== "search" &&
				tab !== "trace" &&
				uneven &&
				data.comparison &&
				data.range && (
					<div className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber">
						<strong>Comparison covers a different number of days.</strong> This range
						has {data.range.days_covered} day(s) of data, the comparison has{" "}
						{data.comparison.days_covered}. Deltas therefore show the{" "}
						<strong>per-day rate</strong>, which is comparable, rather than raw
						totals.
					</div>
				)}

			{tab !== "search" && tab !== "trace" && <CoverageNotice data={data} />}

			{manageOpen && (
				<div className="mb-3">
					<TrafficConnectorsPanel
						websiteId={websiteId}
						onChanged={() => setReloadKey((k) => k + 1)}
					/>
				</div>
			)}

			{nothingConnected && !manageOpen && (
				<div className="monitoring__ai-traffic-banner monitoring__ai-traffic-banner--amber mb-3">
					<strong>No traffic source connected yet.</strong> Connect{" "}
					<strong>Cloudflare</strong> to measure AI bot crawling (GA4 cannot see
					bots — it is JavaScript-based and filters them), and <strong>GA4</strong>{" "}
					for AI referral visits, conversions and revenue. They are complementary:
					together they cover the full picture, and cross-signals need both.
				</div>
			)}

			<div
				className="monitoring__ai-traffic-subtabs"
				role="tablist"
				aria-label="AI Traffic sections"
			>
				{SUB_TABS.map((t) => (
					<button
						key={t.key}
						type="button"
						role="tab"
						aria-selected={tab === t.key}
						className={`monitoring__ai-traffic-subtab${
							tab === t.key ? " monitoring__ai-traffic-subtab--active" : ""
						}`}
						onClick={() => setTab(t.key)}
					>
						{t.label}
					</button>
				))}
			</div>

			{tab === "summary" && (
				<SummaryPanel
					websiteId={websiteId}
					range={sel.range}
					compare={sel.compare ?? undefined}
					traffic={data}
					bots={bots}
				/>
			)}

			{tab === "crawlers" && (
				<div className="monitoring__ai-traffic-panel">
					<div className="monitoring__ai-traffic-kpi-grid">
						<TrafficKpiCard
							label={`AI bot crawls (${days}d)`}
							kpi={kpis.crawl_requests}
							connected={cov.has_crawls}
							connectHint="Cloudflare"
							compareLabel={cmpLabel}
							uneven={uneven}
						/>
						<TrafficKpiCard
							label="Unique URLs crawled"
							kpi={kpis.unique_urls}
							connected={cov.has_crawls}
							connectHint="Cloudflare"
							compareLabel={cmpLabel}
							uneven={uneven}
							hint={
								data.crawl_breakdown
									? `${data.crawl_breakdown.content_paths} content pages · ${data.crawl_breakdown.other_paths} assets & other paths${
											coverageDetail?.available
												? ` — sitemap coverage ${coverageDetail.coverage_pct}% of ${coverageDetail.sitemap_pages}`
												: ""
										}`
									: coverageLoading
										? "Counting indexable URLs…"
										: undefined
							}
						/>
						<TrafficKpiCard
							label="Bot hits on 404s"
							kpi={kpis.not_found_requests}
							connected={cov.has_crawls}
							connectHint="Cloudflare"
							compareLabel={cmpLabel}
							uneven={uneven}
							danger
							hint="Fixing these repairs the surface AI engines try to cite."
						/>
						<TrafficKpiCard
							label="Bot 5xx errors"
							kpi={kpis.server_error_requests}
							connected={cov.has_crawls}
							connectHint="Cloudflare"
							compareLabel={cmpLabel}
							uneven={uneven}
							danger
						/>
					</div>

					{data.status_distribution && (
						<div className="monitoring__ai-traffic-kpi-card">
							<h3 className="monitoring__ai-traffic-section-title">
								Response codes served to AI bots
							</h3>
							<p className="monitoring__ai-traffic-section-note">
								Every 4xx is a page an engine tried to read and could not.
							</p>
							{STATUS_DISTRIBUTION_ROWS.filter(
								([k]) => (data.status_distribution.counts[k] ?? 0) > 0
							).map(([k, label, colour]) => {
								const share = data.status_distribution.shares[k] ?? 0;
								return (
									<div key={k} className="monitoring__ai-traffic-status-row">
										<div className="monitoring__ai-traffic-status-row-head">
											<span>{label}</span>
											<span>
												{share}% ({data.status_distribution.counts[k]})
											</span>
										</div>
										<div className="monitoring__ai-traffic-status-bar-track">
											<div
												className={`monitoring__ai-traffic-status-bar-fill ${colour}`}
												style={{ width: `${share}%` }}
											/>
										</div>
									</div>
								);
							})}
						</div>
					)}

					<div className="monitoring__ai-traffic-kpi-card">
						{cov.has_crawls && Object.keys(data.crawls_by_assistant || {}).length > 0 ? (
							<TrafficEngineTrendChart
								series={toSeries(data.crawls_by_assistant, assistantLabels)}
								ymin={0}
								ymax={axisMax(data.crawls_by_assistant)}
								yLabel="Crawl requests"
								title="Crawls per AI assistant"
								expandable
							/>
						) : (
							<>
								<h3 className="monitoring__ai-traffic-section-title">
									Crawls per AI assistant
								</h3>
								<p className="monitoring__ai-traffic-empty mb-0">
									{cov.has_crawls
										? "No AI crawls recorded yet in this window."
										: "Connect Cloudflare to see which AI crawlers fetch your content."}
								</p>
							</>
						)}
					</div>

					<div className="monitoring__ai-traffic-kpi-card">
						<h3 className="monitoring__ai-traffic-section-title">Broken bot paths</h3>
						<div className="monitoring__table-scroll">
							<table className="monitoring__table">
								<thead>
									<tr>
										<TrafficTableTh>Path</TrafficTableTh>
										<TrafficTableTh>Bot</TrafficTableTh>
										<TrafficTableTh className="monitoring__th-num">Failure</TrafficTableTh>
										<TrafficTableTh className="monitoring__th-num">Failed hits</TrafficTableTh>
									</tr>
								</thead>
								<tbody>
									{(data.broken_bot_paths || [])
										.slice(0, INLINE_ROWS)
										.map((b) => (
											<tr key={`${b.url}-${b.assistant}`}>
												<td>
													<UrlCopyCell url={b.url} error />
												</td>
												<td className="text-muted">
													{assistantLabels[b.assistant] ?? b.assistant}
												</td>
												<td className="monitoring__td-num text-danger fw-semibold">
													{b.status}
												</td>
												<td className="monitoring__td-num">{b.crawl_requests}</td>
											</tr>
										))}
									{(data.broken_bot_paths?.length ?? 0) === 0 && (
										<tr>
											<td
												colSpan={4}
												className="monitoring__ai-traffic-empty text-center py-4"
											>
												{cov.has_crawls
													? "No broken paths hit by AI bots — good."
													: "Connect Cloudflare to populate this."}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
						{(data.broken_bot_paths?.length ?? 0) > INLINE_ROWS && (
							<Button
								size="sm"
								color="primary"
								className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-view-all w-100 mt-2"
								onClick={() => setBrokenOpen(true)}
							>
								View all {data.broken_bot_paths.length}
								{listsTruncated ? "+" : ""} paths
							</Button>
						)}
					</div>
				</div>
			)}

			{tab === "referrals" && (
				<div className="monitoring__ai-traffic-panel">
					<div className="monitoring__ai-traffic-kpi-grid">
						<TrafficKpiCard
							label={`AI referral visits (${days}d)`}
							kpi={kpis.referral_visits}
							connected={cov.has_referrals}
							connectHint="GA4"
							compareLabel={cmpLabel}
							uneven={uneven}
						/>
						<TrafficKpiCard
							label="Engaged sessions"
							kpi={kpis.engaged_sessions}
							connected={cov.has_referrals}
							connectHint="GA4"
							compareLabel={cmpLabel}
							uneven={uneven}
						/>
						<TrafficKpiCard
							label="Conversions from AI"
							kpi={kpis.conversions}
							connected={cov.has_conversions}
							connectHint="GA4"
							compareLabel={cmpLabel}
							uneven={uneven}
						/>
						<TrafficKpiCard
							label="Revenue from AI"
							kpi={kpis.revenue}
							connected={cov.has_conversions}
							connectHint="GA4"
							compareLabel={cmpLabel}
							uneven={uneven}
						/>
						<TrafficCard
							label="Citation → visit CTR"
							hint={`${kpis.citation_to_visit_ctr?.inputs?.referral_visits ?? 0} visits ÷ ${kpis.citation_to_visit_ctr?.inputs?.cited_answers ?? 0} cited answers, same ${days}d window`}
						>
							<div className="monitoring__ai-traffic-kpi-value">
								{kpis.citation_to_visit_ctr?.value == null
									? "—"
									: kpis.citation_to_visit_ctr.value}
								<span className="monitoring__ai-traffic-kpi-unit">%</span>
							</div>
							<div className="monitoring__ai-traffic-kpi-hint">
								Visits from{" "}
								{sourceLabel(
									kpis.citation_to_visit_ctr?.inputs?.visits_source
								) ?? "—"}{" "}
								· citations measured by us
							</div>
						</TrafficCard>
					</div>

					<div className="monitoring__ai-traffic-kpi-card">
						{cov.has_referrals &&
						Object.keys(data.referrals_by_assistant || {}).length > 0 ? (
							<TrafficEngineTrendChart
								series={toSeries(data.referrals_by_assistant, assistantLabels)}
								ymin={0}
								ymax={axisMax(data.referrals_by_assistant)}
								yLabel="Visits"
								title="Referral visits per AI assistant"
								expandable
							/>
						) : (
							<>
								<h3 className="monitoring__ai-traffic-section-title">
									Referral visits per AI assistant
								</h3>
								<p className="monitoring__ai-traffic-empty mb-0">
									{cov.has_referrals
										? "No AI referral visits recorded yet."
										: "Connect GA4 to see humans arriving from AI answers."}
								</p>
							</>
						)}
					</div>

					<div className="monitoring__ai-traffic-kpi-card">
						<h3 className="monitoring__ai-traffic-section-title">
							Top pages by AI referrals
						</h3>
						<p className="monitoring__ai-traffic-section-note">{referralPagesNote}</p>
						<div className="monitoring__table-scroll">
							<table className="monitoring__table">
								<thead>
									<tr>
										<TrafficTableTh>Page</TrafficTableTh>
										<TrafficTableTh>Assistant</TrafficTableTh>
										<TrafficTableTh className="monitoring__th-num">Visits</TrafficTableTh>
										<TrafficTableTh className="monitoring__th-num">Conv.</TrafficTableTh>
									</tr>
								</thead>
								<tbody>
									{(data.top_referral_pages || [])
										.slice(0, INLINE_ROWS)
										.map((p) => (
											<tr key={`${p.url}-${p.assistant}`}>
												<td>
													<UrlCopyCell url={p.url} />
												</td>
												<td className="text-muted">
													{assistantLabels[p.assistant] ?? p.assistant}
												</td>
												<td className="monitoring__td-num">{p.referral_visits}</td>
												<td className="monitoring__td-num">
													{p.conversions ?? "—"}
												</td>
											</tr>
										))}
									{(data.top_referral_pages?.length ?? 0) === 0 && (
										<tr>
											<td
												colSpan={4}
												className="monitoring__ai-traffic-empty text-center py-4"
											>
												{cov.has_referrals
													? "No AI referral pages yet."
													: "Connect GA4 to populate this."}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
						{(data.top_referral_pages?.length ?? 0) > INLINE_ROWS && (
							<Button
								size="sm"
								color="primary"
								className="monitoring__action-btn monitoring__action-btn--primary monitoring__ai-traffic-view-all w-100 mt-2"
								onClick={() => setPagesOpen(true)}
							>
								View all {data.top_referral_pages.length}
								{listsTruncated ? "+" : ""} pages
							</Button>
						)}
					</div>
				</div>
			)}

			{tab === "search" && (
				<SearchPanel
					websiteId={websiteId}
					range={sel.range}
					compare={sel.compare ?? undefined}
				/>
			)}

			{tab === "trace" && (
				<TraceabilityPanel
					websiteId={websiteId}
					range={sel.range}
					bots={bots}
				/>
			)}

			{tab !== "search" && tab !== "trace" && data.last_sync && (
				<p className="monitoring__ai-traffic-last-sync mb-0">
					Last sync {data.last_sync.at ? fmtDateTime(data.last_sync.at) : "—"}
					{data.last_sync.source
						? ` · ${sourceLabel(data.last_sync.source)}`
						: ""}
					{data.last_sync.status ? ` · ${data.last_sync.status}` : ""}
					{data.last_sync.partial
						? " · partial (vendor truncated the window)"
						: ""}
					{(data.last_sync.warnings?.length ?? 0) > 0 && (
						<>
							{" · "}
							<span className="monitoring__ai-traffic-last-sync-warning">
								{data.last_sync.warnings[0]}
							</span>
						</>
					)}
				</p>
			)}

			{brokenOpen && (
				<TrafficListDialog
					title="Broken bot paths"
					detail="URLs AI crawlers requested that did not return content. Counts are the failing hits only, so a path that mostly serves fine shows just its failures — 403/429 means the crawler was refused or throttled rather than the page being missing."
					rows={data.broken_bot_paths || []}
					unit="path"
					pageSize={INLINE_ROWS}
					rowKey={(b) => `${b.url}-${b.assistant}`}
					searchText={(b) =>
						`${b.url} ${assistantLabels[b.assistant] ?? b.assistant} ${b.status ?? ""}`
					}
					copyText={(b) => b.url}
					onClose={() => setBrokenOpen(false)}
					columns={[
						{
							key: "url",
							label: "Path",
							render: (b) => <UrlCopyCell url={b.url} error />,
						},
						{
							key: "bot",
							label: "Bot",
							render: (b) => assistantLabels[b.assistant] ?? b.assistant,
						},
						{
							key: "status",
							label: "Failure",
							align: "right",
							render: (b) => b.status,
						},
						{
							key: "hits",
							label: "Failed hits",
							align: "right",
							render: (b) => b.crawl_requests,
						},
					]}
				/>
			)}

			{pagesOpen && (
				<TrafficListDialog
					title="Top pages by AI referrals"
					detail={referralPagesNote}
					rows={data.top_referral_pages || []}
					unit="page"
					pageSize={INLINE_ROWS}
					rowKey={(p) => `${p.url}-${p.assistant}`}
					searchText={(p) =>
						`${p.url} ${assistantLabels[p.assistant] ?? p.assistant}`
					}
					copyText={(p) => p.url}
					onClose={() => setPagesOpen(false)}
					columns={[
						{
							key: "url",
							label: "Page",
							render: (p) => <UrlCopyCell url={p.url} />,
						},
						{
							key: "assistant",
							label: "Assistant",
							render: (p) => assistantLabels[p.assistant] ?? p.assistant,
						},
						{
							key: "visits",
							label: "Visits",
							align: "right",
							render: (p) => p.referral_visits,
						},
						{
							key: "conv",
							label: "Conv.",
							align: "right",
							render: (p) => p.conversions ?? "—",
						},
					]}
				/>
			)}
		</div>
	);
}
